import {
  DvPreviewerToolPanelComponent,
  DvToolbarAction,
  DvToolbarActionEvent,
} from '@devinco/ui/grid';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TabBase } from '@core/base/tab.base';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { environment } from '@env/environment';
import { ColumnType } from '@features/vt-grid/grid-options/column-types';
import { GridOptionsService } from '@features/vt-grid/grid-options/grid-options.service';
import { FilterModelWithKeys } from '@features/vt-grid/interfaces/grid-context';
import { GridOptions } from '@features/vt-grid/interfaces/grid-options';
import { SplitterWithGridHelperService } from '@features/vt-grid/services/splitter-with-grid-helper.service';
import { Entity } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { TranslateService } from '@ngx-translate/core';
import {
  AssetData,
  AssetDto,
  ToolPanel,
} from '@pages/assets-page/components/interfaces';
import GridUtils from '@features/vt-grid/utils/grid.utils';
import { fileUploadModalConfig } from '@shared/components/file-upload-dialog/file-upload-dialog.config';
import { AttachmentsService } from '@shared/services/attachments.service';
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  IRowNode,
  ModelUpdatedEvent,
  SelectionChangedEvent,
  ToolPanelDef,
  ToolPanelVisibleChangedEvent,
} from 'ag-grid-community';
import { filter, forkJoin, Observable, switchMap, take } from 'rxjs';
import { AssetPictureDocumentGridService } from './asset-picture-document-grid.service';
import { AssetPictureDocumentDto } from './interfaces';
import { SnackBarConfig } from '@shared/constants/snackbar-config';
import { DialogService } from '@core/services/dialog.service';
import { DvSnackbarService } from '@devinco/ui/dds/snackbar';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-asset-picture-document-tab',
  templateUrl: './asset-picture-document-tab.component.html',
  providers: [AssetPictureDocumentGridService, GridOptionsService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AssetPictureDocumentTabComponent
  extends TabBase
  implements OnInit
{
  private gridService = inject(AssetPictureDocumentGridService);
  private gridOptionsService = inject(GridOptionsService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private entityService = inject(SplitterWithGridHelperService);
  private api = inject(RestApiService);
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private snackBar = inject(DvSnackbarService);
  private attachmentsService = inject(AttachmentsService);

  gridOptions?: GridOptions;
  actions = signal<DvToolbarAction[]>([]);
  selectedNode: IRowNode | undefined;
  entity: typeof Entity = Entity;

  private isToolPanelOpen: boolean;
  private gridApi: GridApi | undefined;
  private destroyRef = inject(DestroyRef);
  private colDefs: ColDef[];
  private hasEditPermission = false;

  constructor() {
    super(inject(ActivatedRoute).parent!);
  }

  ngOnInit(): void {
    this.checkPermissions();
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
  }

  onSelectionChanged({ api }: SelectionChangedEvent): void {
    this.selectedNode = api.getSelectedNodes()[0] as IRowNode | undefined;
    const asset = this.selectedNode?.data as AssetData | undefined;

    this.actions.update(actions =>
      actions.map(action => {
        if (action.icon === 'download') {
          return { ...action, disabled: !this.selectedNode };
        }
        if (action.icon === 'delete') {
          return {
            ...action,
            disabled: !this.selectedNode || !this.hasEditPermission,
          };
        }
        return action;
      })
    );

    this.toggleAddActionByEditPermission(this.hasEditPermission);

    if (asset?.DocumentID && this.isToolPanelOpen) {
      this.attachmentsService.getAttachmentPreview(
        api!,
        this.selectedNode?.data.DocumentID,
        asset.FileName
      );
    }
  }

  onModelUpdated(event: ModelUpdatedEvent): void {
    const rowNodes: IRowNode<AssetPictureDocumentDto>[] = [];
    event.api.forEachNode(rowNode => {
      rowNodes.push(rowNode);
    });
    const mainPictureNode = rowNodes.find(node => node.data?.IsDefault);
    if (mainPictureNode && mainPictureNode.data?.IsDefault) {
      event.api
        .getRowNode(String(mainPictureNode.data.DocumentID))
        ?.setSelected(true);
    } else {
      event.api.getDisplayedRowAtIndex(0)?.setSelected(true);
    }
  }

  toolPanelVisibilityChanged(event: ToolPanelVisibleChangedEvent): void {
    this.isToolPanelOpen = event.api.isToolPanelShowing();
    const assetData = this.selectedNode?.data;

    if (this.selectedNode && assetData?.DocumentID && this.isToolPanelOpen) {
      this.attachmentsService.getAttachmentPreview(
        this.gridApi!,
        assetData.DocumentID,
        assetData.FileName
      );
    }
  }

  private checkPermissions(): void {
    forkJoin({
      hasEditPermission: this.permissionsService.hasPermissions(
        [
          RolesAndPermissions.AssetEdit,
          RolesAndPermissions.AssetEditRestricted,
        ],
        false
      ),
      hasAssetEdit: this.permissionsService.hasPermissions([
        RolesAndPermissions.AssetEdit,
      ]),
    })
      .pipe(take(1))
      .subscribe(({ hasEditPermission, hasAssetEdit }) => {
        this.hasEditPermission = hasEditPermission;

        this.colDefs = this.gridService.getColumnDefs(
          this.standardPictureValueChangesFn,
          hasAssetEdit
        );

        this.actions.set(
          this.gridService.getToolbarActions(this.uploadFile, this.deleteFile)
        );

        this.toggleAddActionByEditPermission(hasEditPermission);
        this.listenToEntityChanges();
      });
  }

  private toggleAddActionByEditPermission(hasEditPermission: boolean): void {
    this.actions.update(actions =>
      actions.map(action => {
        if (action.icon === 'add_box') {
          return { ...action, disabled: !hasEditPermission };
        }
        return action;
      })
    );
  }

  private uploadFile = (): void => {
    this.dialogService
      .open(
        fileUploadModalConfig({
          entityId: this.entityId$.getValue(),
          url: `${environment.endpoints.restApi}/attachment/${
            Entity.Asset
          }/${this.entityId$.getValue()}`,
        })
      )
      .closed.pipe(take(1))
      .subscribe(({ responseData }) => {
        if (responseData) {
          this.gridOptions?.context.loadClientSideData?.(
            this.gridOptions.context.extraFilter,
            true
          );
          this.updateSelectedAssetData();
        }
      });
  };

  private standardPictureValueChangesFn = (selectedNodeId?: number): void => {
    if (selectedNodeId !== this.selectedNode?.data.AssetDocumentID) {
      this.api
        .upsert(Entity.Asset, this.entityId$.getValue(), {
          ImageDocumentID: selectedNodeId,
        })
        .pipe(take(1))
        .subscribe(_ => {
          this.entityService.refreshRowData(this.entityId$.getValue());
        });
    }
  };

  private listenToEntityChanges(): void {
    this.entityId$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(entityId => !!entityId)
      )
      .subscribe(id => {
        if (id.includes('_')) return;
        this.selectedNode = undefined;
        this.setupGridOptions();
        (
          this.gridApi?.getToolPanelInstance('preview') as ToolPanel | undefined
        )?.api.setMediaType('');
        this.setupGridToolPanel();
      });
  }

  private getFilterModel(filterId: string): FilterModelWithKeys {
    return {
      AssetID: {
        filterType: 'number',
        type: 'equals',
        filter: filterId,
      },
    };
  }

  private setupGridToolPanel(): void {
    GridUtils.addToolPanelIfMissing(
      this.gridOptions,
      {
        id: 'preview',
        labelDefault: 'Preview',
        labelKey: 'Preview',
        iconKey: 'preview',
        toolPanel: DvPreviewerToolPanelComponent,
        width: 400,
      } as ToolPanelDef,
      '<span class="dv-icons">eye</span>'
    );
  }

  private deleteFile = (event?: DvToolbarActionEvent): void => {
    this.attachmentsService
      .openAttachmentDeleteDialog()
      .closed.subscribe(({ source }) => {
        if (event && source === 'confirm') {
          const { api } = event;
          const colDefs = api.getColumns()?.map(c => c.getColDef());
          const { field } =
            colDefs?.find(
              c => c.type?.includes(ColumnType.Id) || c.type?.[0] === 'id'
            ) || {};
          if (!field) return;
          const selectedNodes: IRowNode[] = api.getSelectedNodes();
          if (!selectedNodes.length) {
            return;
          }
          const { data, rowIndex } = selectedNodes[0];

          this.api
            .deleteCustom(`Attachment/${data[field]}`)
            .pipe(switchMap(() => this.updateSelectedAssetData()))
            .subscribe(() => {
              if (data.IsDefault) {
                this.entityService.refreshRowData(this.entityId$.getValue());
              }
              this.snackBar.open(
                this.translate.instant('General.DeleteActionSuccessful'),
                SnackBarConfig.Success
              );
              api.applyTransaction({ remove: [data!] });
              const lastIndex = api.getLastDisplayedRowIndex();
              const nextIndex = lastIndex > rowIndex! ? rowIndex! : lastIndex;
              api.getDisplayedRowAtIndex(nextIndex)?.setSelected(true, true);
            });
        }
      });
  };

  private updateSelectedAssetData(): Observable<AssetDto> {
    return this.entityService.refreshRowData(this.entityId$.getValue());
  }

  private setupGridOptions(): void {
    const entityId = this.entityId$.getValue();
    if (!this.gridOptions) {
      const initGridOptions = this.gridOptionsService.clientSide(
        this.colDefs,
        Entity.AssetDocument,
        this.getFilterModel(this.entityId$.getValue())
      );

      this.gridOptions = {
        ...initGridOptions,
        defaultColDef: {
          ...initGridOptions.defaultColDef,
          enableCellChangeFlash: false,
        },
      };
    } else {
      this.gridOptions.context.loadClientSideData?.(
        this.getFilterModel(entityId)
      );
    }

    this.gridOptions.context.extraFilter = this.getFilterModel(entityId);
  }
}
